import type { OnboardingQuestion, OnboardingResponse } from './onboardApi'

/**
 * Everything the questionnaire screen decides, as pure functions.
 *
 * The screen renders **the server's catalogue**, which is the point of
 * `GET /v1/auth/onboard` carrying it: a question can be reworded, added or
 * retired without shipping a client. The price is that this build will one
 * day meet a catalogue it does not fully understand, and the rule for that
 * is the same as the gateway's own rule for answers it does not know —
 * carry it, do not choke on it:
 *
 * - a malformed entry is dropped, never rendered half-built;
 * - a `kind` this build cannot draw keeps its prompt and says so, so the
 *   person sees that something is missing rather than a shorter form;
 * - a stored answer for a question that is not rendered is **not sent
 *   back** and therefore survives, because the save merges per question id.
 * - an option id that is stored but no longer in the catalogue rides along
 *   in the selection and is sent back untouched, so opening the screen and
 *   pressing "Fertig" cannot quietly delete somebody's answer.
 */

/** The kinds this build can draw. Anything else is carried, not rendered. */
export type RenderableKind = 'multi' | 'single'

export function isRenderableKind(kind: string): kind is RenderableKind {
  return kind === 'multi' || kind === 'single'
}

/** Selections per question id, always an array — single-choice holds 0 or 1. */
export type Selections = Record<string, string[]>

function isOption(value: unknown): value is { id: string; label: string } {
  if (typeof value !== 'object' || value === null) return false
  const option = value as Record<string, unknown>
  return typeof option.id === 'string' && option.id !== '' && typeof option.label === 'string'
}

/**
 * The catalogue as it arrived, with anything unusable removed. A question
 * needs an id, a prompt and at least one option to be worth a heading.
 */
export function parseQuestions(raw: unknown): OnboardingQuestion[] {
  if (!Array.isArray(raw)) return []
  const seen = new Set<string>()
  const questions: OnboardingQuestion[] = []
  for (const entry of raw) {
    if (typeof entry !== 'object' || entry === null) continue
    const record = entry as Record<string, unknown>
    const id = record.id
    const prompt = record.prompt
    if (typeof id !== 'string' || id === '' || typeof prompt !== 'string' || prompt === '') continue
    if (seen.has(id)) continue
    const options = Array.isArray(record.options) ? record.options.filter(isOption) : []
    if (options.length === 0) continue
    seen.add(id)
    questions.push({
      id,
      prompt,
      kind: typeof record.kind === 'string' ? record.kind : '',
      options,
    })
  }
  return questions
}

/** One stored answer as a list of option ids, whatever shape it arrived in. */
export function answerToIds(value: unknown): string[] {
  if (typeof value === 'string') return value ? [value] : []
  if (!Array.isArray(value)) return []
  return value.filter((entry): entry is string => typeof entry === 'string' && entry !== '')
}

/** The draft to resume from: every rendered question, prefilled or empty. */
export function selectionsFrom(
  answers: Record<string, unknown> | undefined,
  questions: OnboardingQuestion[],
): Selections {
  const selections: Selections = {}
  for (const question of questions) {
    if (!isRenderableKind(question.kind)) continue
    const stored = answerToIds(answers?.[question.id])
    if (question.kind === 'single') {
      // Two values on a single-choice question would be refused by the
      // gateway (`400 invalid_answers`) the moment they were sent back. A
      // value the catalogue no longer offers is dropped here and only here:
      // a `<select>` cannot show it, and keeping it would mean saving an
      // answer the screen never displayed.
      const known = new Set(question.options.map((option) => option.id))
      selections[question.id] = stored.filter((id) => known.has(id)).slice(0, 1)
    } else {
      // Multi keeps ids the catalogue has retired. They cannot be shown, but
      // they are somebody's answer, and re-saving this screen must not be a
      // way to lose one.
      selections[question.id] = stored
    }
  }
  return selections
}

/** Press an option: multi toggles, single replaces (and de-selects itself). */
export function toggleOption(
  selections: Selections,
  question: OnboardingQuestion,
  optionId: string,
): Selections {
  const current = selections[question.id] ?? []
  if (question.kind === 'single') {
    return { ...selections, [question.id]: current[0] === optionId ? [] : [optionId] }
  }
  const next = current.includes(optionId)
    ? current.filter((id) => id !== optionId)
    : [...current, optionId]
  return { ...selections, [question.id]: next }
}

export function isSelected(selections: Selections, questionId: string, optionId: string): boolean {
  return (selections[questionId] ?? []).includes(optionId)
}

/**
 * The body for `POST /v1/auth/onboard`.
 *
 * Only rendered questions appear. Anything else the account has answered
 * stays stored, because the save merges per question id — which is what
 * makes both a partly answered draft and a catalogue this client is behind
 * safe to save over.
 */
export function answersToSend(selections: Selections, questions: OnboardingQuestion[]): Selections {
  const answers: Selections = {}
  for (const question of questions) {
    if (!isRenderableKind(question.kind)) continue
    answers[question.id] = selections[question.id] ?? []
  }
  return answers
}

/** How many of the rendered questions carry an answer — the progress line. */
export function answeredCount(selections: Selections, questions: OnboardingQuestion[]): number {
  return questions.filter(
    (question) => isRenderableKind(question.kind) && (selections[question.id] ?? []).length > 0,
  ).length
}

export function renderableCount(questions: OnboardingQuestion[]): number {
  return questions.filter((question) => isRenderableKind(question.kind)).length
}

/**
 * Whether the screen should open on the questions rather than on the name
 * step: somebody who has already answered came back to change something,
 * and making them walk past their own name again is a step for nothing.
 */
export function opensOnQuestions(state: OnboardingResponse | undefined): boolean {
  return state?.status === 'answered' || state?.status === 'started'
}
